/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';

const ContentItemModel = types.model('ContentItemModel', {
  fileId: types.maybeNull(types.string),
  fileName: types.maybeNull(types.string),
  fileType: types.maybeNull(types.string),
  fileSize: types.maybeNull(types.number),
  status: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  contentPreview: types.maybeNull(types.string),
  uploadedS3Path: types.maybeNull(types.string),
  indexDocumentId: types.maybeNull(types.string)
});

const ContentUploadModel = types
  .model('ContentUploadModel', {
    contents: types.optional(types.array(ContentItemModel), []),
    uploadError: types.maybeNull(types.string),
    loadError: types.maybeNull(types.string),
    deleteError: types.maybeNull(types.string),
    lastUploadedFile: types.maybeNull(ContentItemModel)
  })
  .actions((self) => ({
    uploadContent(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        uploadError: null
      });
      Framework.getEventManager().publish(constants.UPLOAD_CONTENT, { payload });
    },

    setUploadSuccess(data) {
      const newContent = {
        fileId: data.fileId,
        fileName: data.fileName,
        fileType: data.fileName?.split('.').pop() || 'txt',
        fileSize: data.fileSize || 0,
        status: constants.CONTENT_STATUS_PROCESSING,
        createdAt: new Date().toISOString(),
        contentPreview: data.contentPreview || null,
        uploadedS3Path: data.uploadedS3Path,
        indexDocumentId: data.indexDocumentId ?? null
      };

      applySnapshot(self, {
        ...getSnapshot(self),
        uploadError: null,
        lastUploadedFile: newContent,
        contents: [...getSnapshot(self).contents, newContent]
      });
    },

    setUploadError(error) {
      applySnapshot(self, {
        ...getSnapshot(self),
        uploadError: error
      });
    },

    getUploadedContent(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loadError: null
      });
      Framework.getEventManager().publish(constants.GET_UPLOADED_CONTENT, { payload });
    },

    setContents(data) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loadError: null,
        contents: data || []
      });
    },

    setLoadError(error) {
      applySnapshot(self, {
        ...getSnapshot(self),
        loadError: error
      });
    },

    clearLastUploadedFile() {
      applySnapshot(self, {
        ...getSnapshot(self),
        lastUploadedFile: null
      });
    },

    clearContents() {
      applySnapshot(self, {
        ...getSnapshot(self),
        contents: [],
        loadError: null,
        lastUploadedFile: null
      });
    },

    deleteContent(payload) {
      applySnapshot(self, {
        ...getSnapshot(self),
        deleteError: null
      });
      Framework.getEventManager().publish(constants.DELETE_CONTENT, { payload });
    },

    setDeleteSuccess(fileId) {
      applySnapshot(self, {
        ...getSnapshot(self),
        deleteError: null,
        contents: getSnapshot(self).contents.filter((item) => item.fileId !== fileId)
      });
    },

    setDeleteError(error) {
      applySnapshot(self, {
        ...getSnapshot(self),
        deleteError: error
      });
    },

    clearErrors() {
      applySnapshot(self, {
        ...getSnapshot(self),
        uploadError: null,
        loadError: null,
        deleteError: null
      });
    }
  }));

export default ContentUploadModel;
